import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon        from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FileDownloadIcon  from "@mui/icons-material/FileDownload";
import SwapHorizIcon     from "@mui/icons-material/SwapHoriz";
import FilterListIcon    from "@mui/icons-material/FilterList";
import RefreshIcon       from "@mui/icons-material/Refresh";
import { baseapi }       from "../Environmentalurl";
import { entity_id, location, Platform, ApplicationType } from "../Utils/Const";
import useIPAddress      from "../Utils/useIPAddress";
import api               from "../Utils/Api";

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  accent:    "#6366F1",
  accentSoft:"#EEF2FF",
  green:     "#059669",
  greenSoft: "#D1FAE5",
  amber:     "#D97706",
  amberSoft: "#FEF3C7",
  red:       "#DC2626",
  redSoft:   "#FEE2E2",
  bg:        "#F8FAFC",
  border:    "#E2E8F0",
  muted:     "#64748B",
  text:      "#1E293B",
  surface:   "#FFFFFF",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const today    = () => new Date().toISOString().split("T")[0];
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split("T")[0];
};

const fmtAmount = (n) =>
  n !== undefined && n !== null && n !== "" && Number(n) !== 0
    ? `₹${parseFloat(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "—";

const fmtDT = (dt) => {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dt;
  }
};

const normStatus = (s = "") => s.toString().trim().toUpperCase();

const StatusChip = ({ status }) => {
  const map = {
    SUCCESS:  { label: "Success", bg: C.greenSoft, color: C.green },
    CREDIT:   { label: "Credit",  bg: C.greenSoft, color: C.green },
    DEBIT:    { label: "Debit",   bg: C.redSoft,   color: C.red   },
    PENDING:  { label: "Pending", bg: C.amberSoft, color: C.amber },
    FAILED:   { label: "Failed",  bg: C.redSoft,   color: C.red   },
  };
  const key = normStatus(status);
  const s   = map[key] || { label: status || "—", bg: C.accentSoft, color: C.accent };
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        background: s.bg, color: s.color,
        fontWeight: 700, fontSize: 11,
        height: 22, borderRadius: "6px",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

// ── Component ──────────────────────────────────────────────────────────────────
export default function MerchantTransactions() {
  const ip = useIPAddress();

  // ── Read session values ────────────────────────────────────────────────────
  const mobile    = sessionStorage.getItem("mobileNumber") || "";
  const sessionId = (() => {
    try { return JSON.parse(sessionStorage.getItem("SessionID")) || ""; }
    catch { return sessionStorage.getItem("SessionID") || ""; }
  })();
  const merchantToken = sessionStorage.getItem("MerchantToken") || "";

  // ── Date state ─────────────────────────────────────────────────────────────
  const [fromDate,  setFromDate]  = useState(monthAgo());
  const [toDate,    setToDate]    = useState(today());
  const [dateError, setDateError] = useState("");

  // ── Data state ─────────────────────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [apiMsg,  setApiMsg]  = useState("");

  // ── Filter state ───────────────────────────────────────────────────────────
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [filterAnchor, setFilterAnchor] = useState(null);

  // ── Summary (computed from rows) ───────────────────────────────────────────
  const [summary, setSummary] = useState({
    totalCredit:  "0.00",
    totalDebit:   "0.00",
    count:        0,
    closingBal:   "0.00",
  });

  // ── Validate dates ─────────────────────────────────────────────────────────
  const validateDates = () => {
    if (fromDate && toDate && fromDate > toDate) {
      setDateError("From Date cannot be after To Date");
      return false;
    }
    setDateError("");
    return true;
  };

  // ── Fetch transactions ─────────────────────────────────────────────────────
  const fetchTransactions = async () => {
    if (!validateDates()) return;
    setLoading(true);
    setApiMsg("");
    setRows([]);
    setSearch("");
    setStatusFilter("ALL");

    try {
      const response = await api.post(
        `${baseapi}/JanAadhaareWalletStatement`,
        {
          ReqType:         "Jan_Aadhaar_eWallet_Statement",
          Mobilenumber:    mobile,
          ServiceID:       "5",
          MachineID:       ip,
          Platform:        Platform,
          ApplicationType: ApplicationType,
          GeoLocation:     location,
          EntityID:        entity_id,
          SessionID:       sessionId,
          FromDate:        fromDate || "",
          ToDate:          toDate   || "",
        },
        {
          // ✅ Bearer token from sessionStorage (stored after 5555 OTP response)
          headers: {
            Authorization: `Bearer ${merchantToken}`,
          },
        },
      );

      const data = response.data;

      if (data.Response_Code === "1000" && Array.isArray(data.Info)) {
        const tableRows = data.Info;
        setRows(tableRows);
        setFetched(true);

        // Compute summary
        const totalCredit = tableRows.reduce((s, r) => s + (parseFloat(r.Credit) || 0), 0);
        const totalDebit  = tableRows.reduce((s, r) => s + (parseFloat(r.Debit)  || 0), 0);
        const lastRow     = tableRows[tableRows.length - 1];

        setSummary({
          totalCredit:  totalCredit.toFixed(2),
          totalDebit:   totalDebit.toFixed(2),
          count:        tableRows.length,
          closingBal:   lastRow?.Balance || "0.00",
        });
      } else {
        setApiMsg(data.Message || "No transactions found for selected date range.");
        setFetched(true);
        setSummary({ totalCredit: "0.00", totalDebit: "0.00", count: 0, closingBal: "0.00" });
      }
    } catch (err) {
      console.error("MerchantTransactions fetch error:", err);
      setApiMsg("Failed to fetch transactions. Please try again.");
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  // ── Filtered rows ──────────────────────────────────────────────────────────
  const filtered = rows.filter((r) => {
    const matchSearch = !search || [
      r.TransactionID   || "",
      r.TransactionType || "",
      r.TransactionRemarks || "",
      r.OrderID         || "",
      fmtDT(r.TrxnDate),
    ].some((f) => f.toLowerCase().includes(search.toLowerCase()));

    const matchStatus =
      statusFilter === "ALL" ||
      normStatus(r.TrxnStatus) === statusFilter;

    return matchSearch && matchStatus;
  });

  // ── CSV Export ─────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!filtered.length) return;
    const headers = ["Date","Transaction ID","Description","Type","Credit","Debit","Balance","Status","Order ID"];
    const csvRows = filtered.map((r) => [
      `"${fmtDT(r.TrxnDate)}"`,
      r.TransactionID || "—",
      `"${(r.TransactionRemarks || "").replace(/"/g, "'")}"`,
      r.TransactionType || "—",
      r.Credit  || "0",
      r.Debit   || "0",
      r.Balance || "0",
      normStatus(r.TrxnStatus),
      r.OrderID || "—",
    ].join(","));
    const csv  = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `transactions_${fromDate}_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>

      {/* ── Date Filter Card ──────────────────────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${C.border}`,
          borderRadius: "16px",
          p: 2.5,
          mb: 2.5,
          background: C.surface,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mb: 2 }}>
          Transaction History
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end" }}>
          {/* From Date */}
          <Box sx={{ flex: "1 1 180px", minWidth: 160 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.8 }}>
              From Date
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setDateError(""); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ fontSize: 15, color: C.muted }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "10px", fontSize: 13, height: 40 },
              }}
              inputProps={{ max: today() }}
            />
          </Box>

          {/* To Date */}
          <Box sx={{ flex: "1 1 180px", minWidth: 160 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.8 }}>
              To Date
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setDateError(""); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ fontSize: 15, color: C.muted }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "10px", fontSize: 13, height: 40 },
              }}
              inputProps={{ max: today() }}
            />
          </Box>

          {/* Fetch Button */}
          <Button
            variant="contained"
            onClick={fetchTransactions}
            disabled={loading}
            startIcon={
              loading
                ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                : <RefreshIcon sx={{ fontSize: 18 }} />
            }
            sx={{
              borderRadius: "10px",
              height: 40,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              px: 3,
              background: loading
                ? "#e5e7eb"
                : "linear-gradient(135deg, #6366F1, #4F46E5)",
              boxShadow: loading ? "none" : "0 4px 12px rgba(99,102,241,0.35)",
              "&:hover": { background: "linear-gradient(135deg, #4F46E5, #4338CA)" },
            }}
          >
            {loading ? "Fetching..." : "Fetch Transactions"}
          </Button>
        </Box>

        {/* Leave dates empty = fetch all */}
        <Typography sx={{ fontSize: 11, color: C.muted, mt: 1 }}>
          Leave dates empty to fetch all transactions
        </Typography>
        {dateError && (
          <Typography sx={{ fontSize: 12, color: C.red, mt: 0.5 }}>
            ⚠ {dateError}
          </Typography>
        )}
      </Card>

      {/* ── Summary Cards ─────────────────────────────────────────────────── */}
      {fetched && rows.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            gap: 2,
            mb: 2.5,
          }}
        >
          {[
            { label: "Total Credit",  value: `₹${parseFloat(summary.totalCredit).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,  color: C.green  },
            { label: "Total Debit",   value: `₹${parseFloat(summary.totalDebit).toLocaleString("en-IN",  { minimumFractionDigits: 2 })}`,   color: C.red    },
            { label: "Closing Bal",   value: `₹${parseFloat(summary.closingBal).toLocaleString("en-IN",  { minimumFractionDigits: 2 })}`,   color: C.accent },
            { label: "Total Records", value: summary.count,                                                                                   color: C.text   },
          ].map((s) => (
            <Card
              key={s.label}
              elevation={0}
              sx={{ border: `1px solid ${C.border}`, borderRadius: "14px", p: 2, background: C.surface }}
            >
              <Typography sx={{ fontSize: 11, color: C.muted, fontWeight: 600, mb: 0.5 }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: 19, fontWeight: 800, color: s.color }}>
                {s.value}
              </Typography>
            </Card>
          ))}
        </Box>
      )}

      {/* ── Results Table ─────────────────────────────────────────────────── */}
      {fetched && (
        <Card
          elevation={0}
          sx={{ border: `1px solid ${C.border}`, borderRadius: "16px", overflow: "hidden" }}
        >
          {/* Toolbar */}
          <Box
            sx={{
              px: 2.5, py: 1.8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
              Transactions
              <Typography component="span" sx={{ ml: 1, fontSize: 11, color: C.muted, fontWeight: 400 }}>
                {fromDate && toDate ? `${fromDate} → ${toDate}` : "All time"}
              </Typography>
            </Typography>

            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search ID, remarks, type..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 15, color: C.muted }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: "10px", fontSize: 13, height: 36 },
                }}
                sx={{ width: { xs: 180, md: 240 } }}
              />

              {/* Status filter */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
                onClick={(e) => setFilterAnchor(e.currentTarget)}
                sx={{
                  borderRadius: "10px", textTransform: "none", fontSize: 12,
                  height: 36, borderColor: C.border, color: C.muted,
                  "&:hover": { borderColor: C.accent, color: C.accent },
                }}
              >
                {statusFilter === "ALL" ? "All Status" : statusFilter}
              </Button>
              <Menu
                anchorEl={filterAnchor}
                open={Boolean(filterAnchor)}
                onClose={() => setFilterAnchor(null)}
                PaperProps={{ sx: { borderRadius: "12px", minWidth: 140, mt: 0.5 } }}
              >
                {["ALL", "SUCCESS", "CREDIT", "DEBIT", "PENDING", "FAILED"].map((s) => (
                  <MenuItem
                    key={s}
                    onClick={() => { setStatusFilter(s); setFilterAnchor(null); }}
                    sx={{
                      fontSize: 13,
                      fontWeight: statusFilter === s ? 700 : 400,
                      color: statusFilter === s ? C.accent : C.text,
                    }}
                  >
                    {s === "ALL" ? "All Status" : s.charAt(0) + s.slice(1).toLowerCase()}
                  </MenuItem>
                ))}
              </Menu>

              {/* Export CSV */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon sx={{ fontSize: 15 }} />}
                onClick={handleExport}
                disabled={!filtered.length}
                sx={{
                  borderRadius: "10px", textTransform: "none", fontSize: 12,
                  height: 36, borderColor: C.border, color: C.muted,
                  "&:hover": { borderColor: C.accent, color: C.accent },
                }}
              >
                Export CSV
              </Button>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: C.bg }}>
                  {[
                    "Date & Time",
                    "Transaction ID",
                    "Description",
                    "Type",
                    "Credit",
                    "Debit",
                    "Balance",
                    "Status",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontSize: 11, fontWeight: 700, color: C.muted,
                        py: 1.5, letterSpacing: 0.4, textTransform: "uppercase",
                        borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* No data */}
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <SwapHorizIcon sx={{ fontSize: 40, color: C.border, mb: 1, display: "block", mx: "auto" }} />
                      <Typography sx={{ fontSize: 14, color: C.muted, fontWeight: 600 }}>
                        {apiMsg || "No transactions found for this date range"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.5 }}>
                        Try adjusting the date range and fetch again
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ fontSize: 13, color: C.muted }}>
                        No results match your search or filter
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r, i) => (
                    <TableRow
                      key={r.TransactionID || i}
                      sx={{
                        "&:hover": { background: "#F8FAFF" },
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {/* Date & Time */}
                      <TableCell sx={{ py: 1.8, whiteSpace: "nowrap" }}>
                        <Typography sx={{ fontSize: 12, color: C.text }}>
                          {fmtDT(r.TrxnDate)}
                        </Typography>
                      </TableCell>

                      {/* Transaction ID */}
                      <TableCell sx={{ py: 1.8 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: "monospace", color: C.accent }}>
                          {r.TransactionID || "—"}
                        </Typography>
                        {r.OrderID && (
                          <Typography sx={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>
                            {r.OrderID}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Description / Remarks */}
                      <TableCell sx={{ py: 1.8, maxWidth: 200 }}>
                        <Typography
                          sx={{
                            fontSize: 12, color: C.text,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}
                          title={r.TransactionRemarks || ""}
                        >
                          {r.TransactionRemarks || "—"}
                        </Typography>
                      </TableCell>

                      {/* Transaction Type */}
                      <TableCell sx={{ py: 1.8 }}>
                        <Typography sx={{ fontSize: 12, color: C.muted }}>
                          {r.TransactionType || "—"}
                        </Typography>
                      </TableCell>

                      {/* Credit */}
                      <TableCell sx={{ py: 1.8 }}>
                        <Typography sx={{
                          fontSize: 13, fontWeight: 700,
                          color: parseFloat(r.Credit) > 0 ? C.green : C.muted,
                        }}>
                          {fmtAmount(r.Credit)}
                        </Typography>
                      </TableCell>

                      {/* Debit */}
                      <TableCell sx={{ py: 1.8 }}>
                        <Typography sx={{
                          fontSize: 13, fontWeight: 700,
                          color: parseFloat(r.Debit) > 0 ? C.red : C.muted,
                        }}>
                          {fmtAmount(r.Debit)}
                        </Typography>
                      </TableCell>

                      {/* Balance */}
                      <TableCell sx={{ py: 1.8 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.text }}>
                          {fmtAmount(r.Balance)}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ py: 1.8 }}>
                        <StatusChip status={r.TrxnStatus} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer */}
          <Box
            sx={{
              px: 2.5, py: 1.5,
              background: C.bg,
              borderTop: `1px solid ${C.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              {filtered.length} of {rows.length} records
            </Typography>
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              Mobile: {mobile}
            </Typography>
          </Box>
        </Card>
      )}

      {/* ── Initial state ─────────────────────────────────────────────────── */}
      {!fetched && !loading && (
        <Card
          elevation={0}
          sx={{
            border: `1px dashed ${C.border}`,
            borderRadius: "16px",
            p: 5,
            textAlign: "center",
            background: C.bg,
          }}
        >
          <SwapHorizIcon sx={{ fontSize: 48, color: C.border, mb: 1.5 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.muted, mb: 0.5 }}>
            Select date range and fetch transactions
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            Leave dates empty to fetch all transactions
          </Typography>
        </Card>
      )}
    </Box>
  );
}